import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useRealtimeEvent } from "../context/RealtimeContext";
import { api, fmtDate, formatErr, label } from "../lib/api";
import { CustomAnswers } from "./Quotations";
import { Attachments, Empty, Field, PageHeader, StatusBadge, UploadButton, inputCls } from "./kit";

const STATUSES = ["pending", "awaiting_payment", "confirmed", "in_progress", "ready_for_review", "completed", "cancelled"];

function Sel({ value, onChange, items, testid, placeholder }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-[#060d20]">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function PaymentBox({ o, onDone }) {
  const [m, setM] = useState(o.payment?.method || "PayPal");
  const [ref, setRef] = useState("");
  const [info, setInfo] = useState({});
  useEffect(() => {
    api.get("/payment-info").then((r) => setInfo(r.data));
  }, []);
  const p = o.payment || {};
  if (p.status === "confirmed") return <p className="text-emerald-300 text-sm" data-testid="payment-confirmed">Payment confirmed via {p.method}.</p>;
  if (p.status === "submitted") return <p className="text-amber-300 text-sm" data-testid="payment-submitted">Payment submitted via {p.method} (ref {p.reference || "-"}). Waiting for admin confirmation.</p>;
  const submit = async () => {
    try {
      onDone((await api.post(`/orders/${o.id}/payment`, { method: m, reference: ref })).data);
      toast.success("Payment submitted for confirmation");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="rounded-xl bg-[#061232] border border-[#007bff]/30 p-4 space-y-3" data-testid="payment-box">
      <p className="font-cond font-bold uppercase tracking-wider">Complete payment · {o.total} {o.currency}</p>
      <div className="grid grid-cols-2 gap-2">{["PayPal", "Wise"].map((x) => <button key={x} onClick={() => setM(x)} className={`py-2 rounded-lg border ${m === x ? "bg-[#007bff] border-[#007bff]" : "border-[#007bff]/30"}`} data-testid={`pay-method-${x.toLowerCase()}`}>{x}</button>)}</div>
      <p className="text-sm text-slate-300 whitespace-pre-wrap">{(m === "PayPal" ? info.paypal : info.wise) || "Our team will share payment details with you in Messages. Once paid, enter your transaction reference below."}</p>
      <Input placeholder="Transaction ID / reference" value={ref} onChange={(e) => setRef(e.target.value)} className={inputCls} data-testid="pay-reference-input" />
      <button onClick={submit} className="btn-electric !py-2 w-full" data-testid="pay-submit-btn">I have paid</button>
    </div>
  );
}

function ProgressForm({ o, onDone }) {
  const [text, setText] = useState("");
  const [pct, setPct] = useState(o.progress || 0);
  const [imgs, setImgs] = useState([]);
  const submit = async () => {
    try {
      onDone((await api.post(`/orders/${o.id}/progress`, { text, progress: Number(pct), images: imgs })).data);
      setText("");
      setImgs([]);
      toast.success("Progress updated");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="rounded-xl bg-[#061232] border border-[#007bff]/30 p-4 space-y-3" data-testid="progress-form">
      <p className="font-cond font-bold uppercase tracking-wider">Post progress update</p>
      <Textarea placeholder="What was done?" value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid="progress-text-input" />
      <div className="flex items-center gap-3"><Input type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} className={`${inputCls} w-24`} data-testid="progress-pct-input" /><span className="text-sm text-slate-400">% complete</span>
        <UploadButton multiple orderId={o.id} onUploaded={(fs) => setImgs([...imgs, ...fs])} testid="progress-upload-btn"><ImagePlus className="w-4 h-4" /> Screenshots ({imgs.length})</UploadButton></div>
      <button onClick={submit} className="btn-electric !py-2" data-testid="progress-submit-btn">Post update</button>
    </div>
  );
}

function AdminControls({ o, grinders, onDone }) {
  const [f, setF] = useState({ status: o.status, eta: o.eta || "", admin_notes: o.admin_notes || "", total: o.total });
  const call = async (fn, msg) => {
    try {
      onDone((await fn()).data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="rounded-xl bg-[#061232] border border-[#007bff]/30 p-4 space-y-3" data-testid="admin-order-controls">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Status"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} items={STATUSES.map((s) => [s, label(s)])} testid="order-status-select" /></Field>
        <Field label="Estimated completion"><Input value={f.eta} onChange={(e) => setF({ ...f, eta: e.target.value })} className={inputCls} data-testid="order-eta-input" /></Field>
        <Field label="Total"><Input type="number" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} className={inputCls} /></Field>
        <Field label="Assign grinder"><Sel value={o.grinder_id} onChange={(v) => call(() => api.post(`/orders/${o.id}/assign`, { grinder_id: v }), "Grinder assigned")} placeholder="Choose grinder"
          items={grinders.map((g) => [g.id, `${g.name} · ${g.active_orders} active${g.online ? " · online" : ""}`])} testid="order-assign-select" /></Field>
        <Field label="Internal notes" full><Textarea value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} className={inputCls} /></Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => call(() => api.put(`/orders/${o.id}`, f), "Order updated")} className="btn-electric !py-2" data-testid="order-save-btn">Save changes</button>
        {o.payment?.status !== "confirmed" && <button onClick={() => call(() => api.post(`/orders/${o.id}/confirm-payment`), "Payment confirmed")} className="btn-ghost !py-2" data-testid="order-confirm-payment-btn">Confirm payment</button>}
        {o.status !== "completed" && <button onClick={() => call(() => api.put(`/orders/${o.id}`, { status: "completed" }), "Order completed")} className="btn-ghost !py-2" data-testid="order-complete-btn">Mark completed</button>}
        {!o.group_chat_id && <button onClick={() => call(async () => { await api.post(`/orders/${o.id}/group-chat`); return api.get(`/orders/${o.id}`); }, "Group chat created")} className="btn-ghost !py-2" data-testid="order-create-group-btn">Create group chat</button>}
      </div>
    </div>
  );
}

function OrderDetail({ o, role, grinders, onDone }) {
  const p = o.payment || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        {[["Service", `${o.service}${o.service_option ? ` — ${o.service_option}` : ""}`], ["Platform", o.platform], ["Game", o.game_version], ["Current", o.current_progress], ["Target", o.target_progress],
          ["ETA", o.eta], role !== "customer" && ["Customer", o.customer_name], role !== "grinder" && ["Grinder", o.grinder_name || "Not assigned"], role !== "grinder" && ["Total", `${o.total} ${o.currency}`],
          role !== "grinder" && ["Payment", `${label(p.status)}${p.method ? ` · ${p.method}` : ""}${p.reference ? ` · ${p.reference}` : ""}`]].filter((r) => r && r[1]).map(([k, v]) => (
          <div key={k}><p className="text-xs text-slate-500 uppercase tracking-wider">{k}</p><p className="text-slate-200 break-words">{v}</p></div>
        ))}
      </div>
      {o.requirements && <p className="text-sm text-slate-300 whitespace-pre-wrap"><span className="text-slate-500">Requirements: </span>{o.requirements}</p>}
      <CustomAnswers items={o.custom} />
      <div><div className="flex justify-between text-sm mb-1"><span>Progress</span><span data-testid="order-progress-value">{o.progress || 0}%</span></div><Progress value={o.progress || 0} className="h-2 bg-[#0b1a3d]" /></div>
      {o.group_chat_id && <Link to={`/dashboard/groups?c=${o.group_chat_id}`} className="btn-ghost !py-2 text-sm" data-testid="order-open-group-btn"><MessageSquare className="w-4 h-4" /> Open order group chat</Link>}
      {role === "customer" && ["awaiting_payment", "pending"].includes(o.status) && <PaymentBox o={o} onDone={onDone} />}
      {role === "grinder" && !o.grinder_accepted && <button onClick={async () => onDone((await api.post(`/orders/${o.id}/accept-task`)).data)} className="btn-electric !py-2" data-testid="grinder-accept-btn">Accept task</button>}
      {role === "grinder" && o.grinder_accepted && !["completed", "ready_for_review", "cancelled"].includes(o.status) && (
        <><ProgressForm o={o} onDone={onDone} /><button onClick={async () => { onDone((await api.post(`/orders/${o.id}/ready`)).data); toast.success("Marked ready for review"); }} className="btn-ghost !py-2" data-testid="grinder-ready-btn">Mark ready for admin review</button></>
      )}
      {role === "admin" && <><AdminControls key={o.updated_at} o={o} grinders={grinders} onDone={onDone} /><ProgressForm o={o} onDone={onDone} /></>}
      <div>
        <div className="flex items-center justify-between"><p className="font-cond font-bold uppercase tracking-wider">Order files</p>
          {role !== "grinder" && <UploadButton multiple orderId={o.id} onUploaded={async (fs) => onDone((await api.post(`/orders/${o.id}/attachments`, { files: fs })).data)} testid="order-attach-btn" />}</div>
        <Attachments items={o.attachments} />
        {!o.attachments?.length && <p className="text-sm text-slate-500">No files yet.</p>}
      </div>
      <div>
        <p className="font-cond font-bold uppercase tracking-wider mb-2">Progress updates</p>
        <ol className="space-y-3 border-l border-[#007bff]/40 pl-4">
          {[...(o.progress_updates || [])].reverse().map((u) => (
            <li key={u.id} className="text-sm"><p className="text-slate-500 text-xs">{fmtDate(u.created_at)} · {u.by} ({u.role}){u.progress != null ? ` · ${u.progress}%` : ""}</p><p className="text-slate-200 whitespace-pre-wrap">{u.text}</p><Attachments items={u.images} /></li>
          ))}
          {!o.progress_updates?.length && <li className="text-sm text-slate-500">No updates yet.</li>}
        </ol>
      </div>
    </div>
  );
}

export default function Orders({ progressOnly }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [sel, setSel] = useState(null);
  const [grinders, setGrinders] = useState([]);
  const load = useCallback(() => api.get("/orders").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    if (user.role === "admin") api.get("/admin/users", { params: { role: "grinder" } }).then((r) => setGrinders(r.data.filter((g) => g.status === "active")));
  }, [load, user.role]);
  useRealtimeEvent((m) => {
    if (m.event === "refresh" || (m.event === "notification" && ["order", "assignment", "payment", "grinder"].includes(m.data.type))) {
      load();
      if (sel) api.get(`/orders/${sel.id}`).then((r) => setSel(r.data)).catch(() => {});
    }
  });
  const base = progressOnly ? items.filter((o) => o.grinder_accepted && !["completed", "cancelled"].includes(o.status)) : items;
  const shown = base.filter((o) => tab === "all" || o.status === tab);
  const onDone = (o) => {
    setSel(o);
    load();
  };
  const title = user.role === "admin" ? "Orders" : user.role === "grinder" ? (progressOnly ? "Order Progress" : "Assigned Orders") : "My Orders";
  return (
    <div data-testid="orders-page">
      <PageHeader title={title} desc={user.role === "grinder" ? "Only orders assigned to you appear here" : "Track status, payments and progress"} />
      {!progressOnly && (
        <div className="flex flex-wrap gap-2 mb-4">
          {["all", ...STATUSES].map((t) => <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full text-sm border ${tab === t ? "bg-[#007bff] border-[#007bff] text-white" : "border-[#007bff]/30 text-slate-300"}`} data-testid={`order-tab-${t}`}>{label(t)} ({t === "all" ? base.length : base.filter((o) => o.status === t).length})</button>)}
        </div>
      )}
      {shown.length === 0 ? <Empty text="No orders here yet." /> : (
        <div className="grid gap-3">
          {shown.map((o) => (
            <button key={o.id} onClick={() => setSel(o)} className="card-neon card-neon-hover p-4 text-left" data-testid={`order-row-${o.number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="font-semibold text-white">{o.number} · {o.service}</p><p className="text-xs text-slate-400">{user.role !== "customer" ? `${o.customer_name} · ` : ""}{o.platform}{o.grinder_name && user.role !== "grinder" ? ` · Grinder: ${o.grinder_name}` : ""}{o.eta ? ` · ETA ${o.eta}` : ""}</p></div>
                <div className="flex items-center gap-2">{user.role === "grinder" && !o.grinder_accepted && <StatusBadge s="new" />}{user.role !== "grinder" && <StatusBadge s={o.payment?.status} />}<StatusBadge s={o.status} testid={`order-status-${o.number}`} /></div>
              </div>
              <Progress value={o.progress || 0} className="h-1.5 mt-3 bg-[#0b1a3d]" />
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scroll-thin bg-[#050a18] border-[#007bff]/40">
          {sel && (<><DialogTitle className="font-display text-3xl text-metal flex flex-wrap items-center gap-3">{sel.number} <StatusBadge s={sel.status} /></DialogTitle>
            <OrderDetail o={sel} role={user.role} grinders={grinders} onDone={onDone} /></>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
